import type { HomeyAPI, Log } from 'homey-api';

import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

export type LogProps = {
  [K in keyof Log as Log[K] extends Function ? never : K]: Log[K];
};

type LogsData = { [key: string]: Log };
type LogsById = { [key: string]: LogProps };

interface State {
  data: LogsData | null;
  byId: LogsById | null;
  isLoading: boolean;
  error: unknown;
}

export type LogsStoreState = State;

export class LogsStore extends BaseApiStore<State, Log> {
  constructor() {
    super('logs');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, silent, cache, abortController }: FetchArgs) {
    if (silent === false) {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.insights, reference: this });

      if (abortController.signal.aborted) return;

      const logsPromise = api.insights.getLogs({
        $cache: cache,
      });

      const [logsData] = await Promise.all([logsPromise]);

      if (abortController.signal.aborted) return;

      const logs = Object.values(logsData);

      const data: LogsData = {};
      const byId: LogsById = {};

      for (const logInstance of logs) {
        const logReference = { ...logInstance };

        data[logReference.id] = logInstance;
        byId[logReference.id] = logReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        isLoading: false,
        error,
      });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.insights, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.insights.on('log.create', this.handleCreate);
    api.insights.on('log.update', this.handleUpdate);
    api.insights.on('log.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.insights.off('log.create', this.handleCreate);
    api.insights.off('log.update', this.handleUpdate);
    api.insights.off('log.delete', this.handleDelete);
  }

  getInstanceFromCache(log: { id: string }) {
    return this.getCache()[log.id];
  }

  getCache(): { [key: string]: Log } {
    const api = this.getApi();
    const cache = api.insights.__cache['log'];
    return cache;
  }

  handleCreate = (createdLog: Log) => {
    this.DEBUG && console.info(`create:log:${createdLog.id}`);
    const state = this.get();
    const logInstance = this.getInstanceFromCache(createdLog);

    if (logInstance == null) return;

    const logReference = { ...logInstance };

    this.set({
      data: {
        ...state.data,
        [createdLog.id]: logInstance,
      },
      byId: {
        ...state.byId,
        [createdLog.id]: logReference,
      },
    });
  };

  handleUpdate = (updatedLog: Log) => {
    this.DEBUG && console.info(`update:log:${updatedLog.id}`);

    this.addToQueue(updatedLog.id, updatedLog);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedLog] of Object.entries(updateQueue)) {
        const logInstance = this.getInstanceFromCache(queuedLog);

        if (state.data == null) {
          this.DEBUG && console.info(`update:log:missing-data`);
          continue;
        }

        if (logInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:log:unknown:${id}`);
          continue;
        }

        const logReference = { ...logInstance };

        nextById[id] = logReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:log:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedLog: Log) => {
    this.DEBUG && console.info(`delete:log:${deletedLog.id}`);
    const state = this.get();

    if (state.data == null) {
      this.DEBUG && console.log(`delete:log:missing-data`);
      return;
    }

    const logInstance = state.data[deletedLog.id];

    if (logInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[logInstance.id];
    delete nextById[logInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const logs = Object.values(cache ?? {});
    const data: LogsData = {};
    const byId: LogsById = {};

    for (const logInstance of logs) {
      const logReference = { ...logInstance };
      data[logReference.id] = logInstance;
      byId[logReference.id] = logReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const logsStore = new LogsStore();
